/**
 * CoachMark — centered dialog (GlowUpDialog style) with a long SVG arrow
 * pointing at the target element. The dialog stays fixed in the viewer center;
 * only the arrow changes between steps. Includes optional extra content
 * (e.g. referral code input on the last creator step).
 *
 * The footer button reads "Next" on every step but the LAST, where it becomes "Got it!" (owner,
 * 20260904) — shared by every walk-through built on this component (the Timeline's Quick Orient
 * and the Script tab's ScriptOrient).
 *
 * THE ARROW (rewritten 20260922 on two owner reports; arrowGeometry.ts carries both in full):
 *
 *  · WHERE IT LANDS. This file used to keep its own `nearestEdgePoint`, which CLAMPED the dialog's
 *    departure corner into the target rect — so a corner far below-left of a 70px sub-tab landed
 *    the tip on `rect.left - 6`, i.e. over the tab to its LEFT. The owner's Prep/Breakdown
 *    screenshot is exactly that: the Cast arrow on Script, the Locations arrow on Cast.
 *    TutorialPointerArrow had already replaced that rule; this file now imports the same one
 *    instead of keeping a second, divergent copy, and `nearestEdgePoint` is gone.
 *
 *  · WHERE IT STARTS. "They attach to the outside of the tutorial panel so it sometimes is hard to
 *    determine where they're starting from … attach them to something inside instead (like the
 *    left or right edge of the call to action text)", and for these dialogs specifically: "For the
 *    Breakdown/Timeline tutorials, the source can be the top header text." So the arrow leaves the
 *    HEADER TITLE's left or right edge (whichever side the target is on) as ONE continuous curve
 *    — the title is marked with data-coach-origin="title", and the arrow layer was raised over the
 *    dialog (CoachMark.module.css) so it paints across the dialog's padding on its way out instead
 *    of disappearing under it. The one exception is a target BELOW the dialog (the Timeline's shot
 *    lane and gutter, with the dialog centred): a curve from the top would have to cross the whole
 *    dialog body to get there, which is precisely the owner's stated reason for switching ends
 *    ("If it covers the text too much we can switch up the starting point"), so for those the
 *    arrow leaves from the Next button's edge along the footer row instead. The step counter moved
 *    out of the header into that footer the same day so neither row has other text for the line
 *    to cross.
 *
 * NATIVE TARGETS (20261002, the Shot Editor coach marks — CanvasOverlay/CanvasOrient.tsx). That walk
 * points at Unity controls under a transparent overlay, through invisible phantom elements Unity
 * positions, which asked four backwards-compatible things of this component. The Timeline's and the
 * Script tab's walks pass none of the new props and behave exactly as before.
 *  · A step whose target is missing now draws NO arrow. It used to keep the previous step's.
 *  · `remeasureKey`: a target can move without a window resize (Unity re-pushes a control's rect as
 *    the shot list slides); a new key re-measures. The arrow is keyed by the step, so a re-measure
 *    within a step never replays its draw-in.
 *  · `avoidTarget`: a native control can sit under the centred dialog; the dialog then moves off it
 *    (arrowGeometry.ts placeDialogClearOfTarget) and the arrow leaves from where it moved to.
 *  · The backdrop is confined to the fold-split touch region via the overlay's region variables
 *    (CoachMark.module.css), which are only ever set on the canvas-overlay page.
 */

import { useEffect, useLayoutEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './CoachMark.module.css';
import {
  buildArrowPath,
  departureOnSide,
  endpointTowardCenter,
  NO_DIALOG_SHIFT,
  offsetEdgeRect,
  originRectWithin,
  pickDeparture,
  placeDialogClearOfTarget,
  rectCenter,
  sideFacingTarget,
  type ArrowPath,
  type DialogShift,
  type EdgeRect,
} from './Tutorial/arrowGeometry';

export type ArrowDirection = 'above' | 'below' | 'left' | 'right';

export interface CoachStep {
  target: string;       // data-orient value to find the element
  title: string;        // bold heading
  text: string;         // description
  arrow: ArrowDirection;
  /** Optional key for rendering custom content (e.g. 'referral') */
  contentKey?: string;
}

interface Props {
  steps: CoachStep[];
  currentStep: number;
  onAdvance: () => void;
  onSkip: () => void;
  /** Render custom content for steps with contentKey */
  renderContent?: (key: string, advance: () => void) => React.ReactNode;
  /** Re-measure the arrow whenever this value changes — for targets that move without a window
   *  resize. Leave undefined to measure on a step change and on resize only (the original rule). */
  remeasureKey?: unknown;
  /** Move the dialog off a target it would otherwise cover (see placeDialogClearOfTarget). Default
   *  false: the dialog stays centred, as it always has. */
  avoidTarget?: boolean;
}

/** The two elements inside the dialog the arrow may depart from — the header title (owner
 *  20260922: "the source can be the top header text"), and the Next button for a target below the
 *  dialog only (see the header comment). The values are the `data-coach-origin` attributes
 *  stamped on those two elements. */
const ORIGIN_TITLE = '[data-coach-origin="title"]';
const ORIGIN_NEXT = '[data-coach-origin="next"]';

interface ArrowPathOptions {
  /** The backdrop the dialog is centred in — the bounds an `avoidTarget` move must stay inside. */
  backdropRef: React.RefObject<HTMLDivElement | null>;
  avoidTarget: boolean;
  remeasureKey: unknown;
}

/** Compute the SVG arrow from the dialog's header title (or, below it, the Next button) to the
 *  target element's edge — and, for an `avoidTarget` host, how far the dialog moves off that target. */
function useArrowPath(
  step: CoachStep | undefined,
  dialogRef: React.RefObject<HTMLDivElement | null>,
  { backdropRef, avoidTarget, remeasureKey }: ArrowPathOptions,
) {
  const [path, setPath] = useState<ArrowPath | null>(null);
  const [shift, setShift] = useState<DialogShift>(NO_DIALOG_SHIFT);
  // The shift the dialog's DOM currently wears. Every rect measured inside the dialog includes it,
  // so it is taken back out to recover the centred box. Updated in a layout effect, i.e. in the
  // same commit the transform itself lands in, so the two never disagree at measure time.
  const appliedShift = useRef<DialogShift>(NO_DIALOG_SHIFT);
  useLayoutEffect(() => { appliedShift.current = shift; }, [shift]);
  const applyShift = useCallback((next: DialogShift) => {
    setShift((prev) => (prev.dx === next.dx && prev.dy === next.dy ? prev : next));
  }, []);

  const measure = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(`[data-orient="${step.target}"]`);
    const dialog = dialogRef.current;
    if (!dialog) return;
    // 20261002 — a step whose target is not in the DOM draws NO arrow. This used to return with
    // the path untouched, so the previous step's arrow stayed up, re-drawn under the new step's
    // copy and pointing at the old control. (With avoidTarget the dialog goes back to the centre:
    // there is nothing left to keep clear of.)
    if (!el) {
      setPath(null);
      if (avoidTarget) applyShift(NO_DIALOG_SHIFT);
      return;
    }

    // For shot-lane, aim at the first shot bar instead of the full wrapper
    const aimEl = step.target === 'shot-lane'
      ? (el.querySelector('[class*="shotBar"]') ?? el)
      : el;
    const targetRect = aimEl.getBoundingClientRect();
    // A display:none target (e.g. the shell's Home button collapsed under
    // PROJECTS_HOME_HIDDEN_QUERY while a step still names it as "projects-home") has a
    // zero-size rect at the viewport origin — bail rather than draw an arrow there.
    if (targetRect.width === 0 || targetRect.height === 0) {
      setPath(null);
      if (avoidTarget) applyShift(NO_DIALOG_SHIFT);
      return;
    }

    // The dialog's box, and the inside origins below, as they will be AFTER this measure: for a
    // host without avoidTarget that is simply where they are (the dialog never moves); with it,
    // the centred box moved by the shift this measure settles on.
    let dialogRect: EdgeRect = dialog.getBoundingClientRect();
    let originMove: DialogShift = NO_DIALOG_SHIFT;
    if (avoidTarget) {
      const held = appliedShift.current;
      const centred = offsetEdgeRect(dialogRect, -held.dx, -held.dy);
      const backdrop = backdropRef.current;
      const next = backdrop
        ? placeDialogClearOfTarget({ dialog: centred, target: targetRect, bounds: backdrop.getBoundingClientRect() })
        : NO_DIALOG_SHIFT;
      dialogRect = offsetEdgeRect(centred, next.dx, next.dy);
      originMove = { dx: next.dx - held.dx, dy: next.dy - held.dy };
      applyShift(next);
    }
    const originWithin = (selector: string): EdgeRect | null => {
      const r = originRectWithin(dialog, selector);
      if (r === null || (originMove.dx === 0 && originMove.dy === 0)) return r;
      return offsetEdgeRect(r, originMove.dx, originMove.dy);
    };
    const targetCenter = rectCenter(targetRect);

    // The header title, unless the target is BELOW the dialog — then the Next button, so the curve
    // leaves along the footer row rather than across the body (header comment). Each falls
    // through to the other when its element cannot be measured (a contentKey step renders custom
    // content instead of the footer; a dialog still being laid out measures zero), and both to a
    // corner departure (originRect null → pickDeparture's own rule), which is the pre-20260922
    // behaviour and always draws something.
    const targetBelow = targetCenter.y > dialogRect.bottom;
    const originRect: EdgeRect | null = targetBelow
      ? (originWithin(ORIGIN_NEXT) ?? originWithin(ORIGIN_TITLE))
      : (originWithin(ORIGIN_TITLE) ?? originWithin(ORIGIN_NEXT));

    // avoidTarget hosts only (20261002): a control lying straight above / below / beside the dialog
    // — which is where a moved dialog always leaves it, 8-48 px off one edge — is reached from the
    // origin's edge FACING it (sideFacingTarget's header has the loop this avoids).
    const facing = avoidTarget ? sideFacingTarget(dialogRect, targetCenter) : null;
    const { point, exitSide } = facing !== null && originRect !== null
      ? departureOnSide(originRect, facing, targetCenter)
      : pickDeparture({
        anchorRect: dialogRect,
        originRect,
        targetCenter,
        // The dialog is centred, so no corner is privileged — nearest-corner scoring is the rule
        // this component has always used, and stays the fallback when nothing inside can be found.
        startCorner: 'auto',
      });
    const tip = endpointTowardCenter(targetRect, point);
    // fullLength for avoidTarget hosts only: a short hook round a nearby control bends back on
    // itself, and the default dash estimate would stop short of its head.
    setPath(buildArrowPath({
      departure: point, exitSide, anchorRect: dialogRect, tip, ...(avoidTarget ? { fullLength: true } : {}),
    }));
  }, [step, dialogRef, backdropRef, avoidTarget, applyShift]);

  useEffect(() => {
    if (!step) return;
    // Delay for dialog to render
    const timer = setTimeout(measure, 80);
    window.addEventListener('resize', measure);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', measure);
    };
    // remeasureKey is a deliberate extra trigger (20261002): a target that moved without a resize.
    // It is undefined, i.e. constant, for every host that does not pass it.
  }, [step, measure, remeasureKey]);

  // avoidTarget hosts only: settle the dialog's place (and the arrow) BEFORE the step's first paint
  // and on every re-measure, rather than 80 ms after — a dialog seen centred over its target and
  // then jumping off it would read as a glitch on every step. The 80 ms pass above still runs and
  // re-confirms once layout has had a frame to settle.
  useLayoutEffect(() => {
    if (!avoidTarget || !step) return;
    // Measuring the laid-out DOM and placing from it before the browser paints is exactly what a
    // layout effect is for (React's useLayoutEffect "measure layout before repaint" pattern).
    // eslint-disable-next-line react-hooks/set-state-in-effect
    measure();
  }, [avoidTarget, step, measure, remeasureKey]);

  return { path, shift };
}

/* The tutorial spotlight literal, shared VERBATIM with TutorialPointerArrow.tsx and the target
   halos (global.css [data-guide-glow], AiWorkspace/SceneBoardExpanded/CoverageReviewDialog
   pulses). Was CoachMark's own older, duller rgba(212, 160, 23) until owner batch 7, 20260903 —
   one arrow cue for every guided surface, and it stays GOLD (not the app accent the card chrome
   moved to) because several tour targets are themselves accent-red buttons. */
const SPOTLIGHT_GOLD = 'rgba(245, 197, 66, 0.8)';

/** Separate component so `key={currentStep}` remounts it cleanly */
function ArrowSvg({ arrowPath }: { arrowPath: ArrowPath }) {
  const drawDuration = Math.max(0.4, arrowPath.len / 600);
  return (
    <svg
      className={styles.arrowSvg}
      viewBox={`0 0 ${arrowPath.w} ${arrowPath.h}`}
      xmlns="http://www.w3.org/2000/svg"
      style={{ '--draw-duration': `${drawDuration}s`, '--draw-len': arrowPath.len } as React.CSSProperties}
    >
      <path
        d={arrowPath.line}
        fill="none"
        stroke={SPOTLIGHT_GOLD}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={styles.arrowLine}
      />
      <path
        d={arrowPath.head}
        fill="none"
        stroke={SPOTLIGHT_GOLD}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={styles.arrowHead}
      />
    </svg>
  );
}

export function CoachMark({
  steps, currentStep, onAdvance, onSkip, renderContent, remeasureKey, avoidTarget = false,
}: Props) {
  const step = steps[currentStep];
  const dialogRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const { path: arrowPath, shift } = useArrowPath(step, dialogRef, { backdropRef, avoidTarget, remeasureKey });
  const { t } = useTranslation('help');

  // Click backdrop to advance (but not clicks on the dialog itself)
  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    // Only advance on backdrop click, not dialog content
    if (e.target === e.currentTarget) {
      onAdvance();
    }
  }, [onAdvance]);

  // Escape to skip
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onSkip(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onSkip]);

  if (!step) return null;

  const hasCustomContent = step.contentKey && renderContent;
  // Only an avoidTarget host ever moves the dialog; every other host renders no style at all.
  const dialogMoved = avoidTarget && (shift.dx !== 0 || shift.dy !== 0);

  return (
    <div
      ref={backdropRef}
      className={styles.backdrop}
      onClick={handleBackdropClick}
    >
      {/* SVG arrow overlay */}
      {arrowPath && <ArrowSvg key={currentStep} arrowPath={arrowPath} />}

      <div
        ref={dialogRef}
        className={styles.dialog}
        style={dialogMoved ? { transform: `translate(${shift.dx}px, ${shift.dy}px)` } : undefined}
        onClick={e => e.stopPropagation()}
      >
        {/* Accent header bar */}
        <div className={styles.header}>
          {/* data-coach-origin: the arrow's departure point (20260922) — the dialog's own
              equivalent of a card's CTA line ("the source can be the top header text"). The span,
              not the header bar: the bar is full-bleed across the dialog, so departing from it
              would put the start back on the dialog's edge, which is the thing the owner asked to
              move away from. The step counter that used to sit at this row's right end lives in
              the footer now, so a right-hand exit crosses nothing but the bar itself. */}
          <span className={styles.title} data-coach-origin="title">{step.title}</span>
        </div>

        <div className={styles.body}>
          <p className={styles.text}>{step.text}</p>

          {hasCustomContent && renderContent!(step.contentKey!, onAdvance)}

          {/* The counter rides the footer (20260922, moved from the header — see the title above).
              A contentKey step renders its own content instead of the footer and keeps the counter
              on a row of its own, so the count is never lost with the button. */}
          {hasCustomContent && (
            <div className={styles.footer}>
              <span className={styles.stepCounter}>{t('coachMarkStepCounter', { current: currentStep + 1, total: steps.length })}</span>
            </div>
          )}
          {!hasCustomContent && (
            <div className={styles.footer}>
              <span className={styles.stepCounter}>{t('coachMarkStepCounter', { current: currentStep + 1, total: steps.length })}</span>
              {/* data-coach-origin="next": the arrow's departure point for a target BELOW the dialog
                  (header comment) — the button itself rather than the footer row, for the same
                  shrink-wrap reason as the title. */}
              <button className={styles.nextBtn} onClick={onAdvance} data-coach-origin="next">
                {t(currentStep >= steps.length - 1 ? 'coachMarkGotIt' : 'coachMarkNext')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
